import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, FileText, Loader2 } from 'lucide-react';

// Components
import {
  StepIndicator,
  FileUploadSection,
  WebsiteCrawlSection,
  ProcessingConfiguration,
  PreviewPanel,
  SuccessStep
} from './components/Create';

// Types
import {
  Step,
  UploadedFile,
  CrawlStatusResponse,
  CrawlRequest,
  ProcessingConfig,
  IndexingEstimate,
} from './types/create';
import { addDocument, crawlWebsite, getDetail, initializeKnowledgeBase, uploadFileData } from 'services/knowledge.service';
import { KnowledgeApiService } from './services/api';
import { simulateApiDelay } from './utils';
import { DataSourceType } from './types';

interface CreateDocumentProps {
  defaultValues?: any;
}

const CreateDocument = ({ defaultValues }: CreateDocumentProps) => {
  const navigate = useNavigate();

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDataSource, setSelectedDataSource] = useState<any>(DataSourceType.WebScraping);
  const [isOptionsExpanded, setIsOptionsExpanded] = useState(true);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // File Upload State
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Website Crawl State
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('jina');
  const [crawlOptions, setCrawlOptions] = useState({
    crawlSubPages: true,
    onlyMainContent: true,
    includes: '',
    excludes: '',
    limit: 10,
    useSitemap: true,
    jinaApiKey: 'jina_16175ed5d83744fb8cfdb26d3813e03exYTgTtPG1f1XMLEKj29sE8BrT1rj',
    firecrawlApiKey: 'jina_16175ed5d83744fb8cfdb26d3813e03exYTgTtPG1f1XMLEKj29sE8BrT1rj'
  });
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlResults, setCrawlResults] = useState<CrawlStatusResponse | null>(null);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState<number>(0);

  // Knowledge Processing State
  const [processingConfig, setProcessingConfig] = useState<ProcessingConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [datasetResponse, setDatasetResponse] = useState<any | null>(null);
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const [knowledgeId, setKnowledgeId] = useState<string | null>(defaultValues?.id || null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (defaultValues !== undefined) {
      getDetail(defaultValues.id).then((data) => {
        setData(data);
      });
    }
  }, [defaultValues]);

  useEffect(() => {
    if (data) {
      setKnowledgeId(data.id || null);
      setSelectedDataSource(data.dataSourceType || null);
    }
  }, [data]);

  // File Upload Functions
  const uploadFile = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadFileData('datasets', file);
      const result = response.data || response;
      setCrawlResults(response);

      const mappedFile: UploadedFile = {
        id: result.id,
        name: result.name || file.name,
        size: result.size || file.size,
        extension: file.name.split('.').pop() || '',
        mime_type: result.contentType || file.type,
        created_by: "current-user",
        created_at: Math.floor(new Date(result.uploadedAt || new Date()).getTime() / 1000),
        preview_url: null
      };

      setUploadedFile(mappedFile);
      return mappedFile;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    return await uploadFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      try {
        await uploadFile(file);
      } catch (error) {
        console.error('Drop upload failed:', error);
      }
    }
  };

  // Website Crawl Functions
  const startWebsiteCrawl = async (provider: string) => {
    if (!websiteUrl.trim()) return;
    setIsCrawling(true);
    setCrawlResults(null);

    const crawlPayload: CrawlRequest = {
      url: websiteUrl,
      options: crawlOptions,
      provider: provider
    };

    if (crawlPayload) {
      crawlWebsite({ url: crawlPayload.url, provider: crawlPayload.provider, options: crawlPayload.options })
        .then((res: any) => {
          setCrawlResults(res);

          setSelectedPreviewDocument(0);
          setSelectedPages(new Set(res.data?.map((_: any, index: number) => index) || []));
          setIsCrawling(false);
          setIsOptionsExpanded(false);

        }).catch((error: any) => {
          console.error('Crawl failed:', error);
          setIsCrawling(false);
        });
    }
  };

  const handlePageSelection = (pageIndex: number, checked: boolean) => {
    const newSelectedPages = new Set(selectedPages);
    if (checked) {
      newSelectedPages.add(pageIndex);
    } else {
      newSelectedPages.delete(pageIndex);
    }
    setSelectedPages(newSelectedPages);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && crawlResults) {
      setSelectedPages(new Set(crawlResults?.data?.map((_, index) => index)));
    } else {
      setSelectedPages(new Set());
    }
  };

  const resetCrawlResults = () => {
    setCrawlResults(null);
    setSelectedPages(new Set());
    setWebsiteUrl('');
  };

  // Knowledge Processing Functions
  const fetchProcessingConfig = async () => {
    setIsLoadingConfig(true);
    try {
      const config = await KnowledgeApiService.fetchProcessingConfig();
      setProcessingConfig(config);
    } catch (error) {
      console.error('Failed to fetch processing config:', error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const fetchIndexingEstimate = async () => {
    setIsLoadingPreview(true);
    try {
      await simulateApiDelay();
      // setPreviewData(null);
    } catch (error) {
      console.error('Failed to fetch indexing estimate:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const fetchWebsiteIndexingEstimate = async () => {
    setIsLoadingPreview(true);
    try {
      await simulateApiDelay();
      // setPreviewData(null);
    } catch (error) {
      console.error('Failed to fetch website indexing estimate:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const updateProcessingRule = (ruleId: string, enabled: boolean) => {
    if (!processingConfig) return;

    setProcessingConfig({
      ...processingConfig,
      rules: {
        ...processingConfig.rules,
        preProcessingRules: processingConfig.rules.preProcessingRules.map(rule =>
          rule.id === ruleId ? { ...rule, enabled } : rule
        )
      }
    });
  };

  const updateSegmentationConfig = (field: string, value: string | number) => {
    if (!processingConfig) return;

    setProcessingConfig({
      ...processingConfig,
      rules: {
        ...processingConfig.rules,
        segmentation: {
          ...processingConfig.rules.segmentation,
          [field]: value
        }
      }
    });
  };

  const updateIndexingMethod = (method: string) => {
    if (!processingConfig) return;

    setProcessingConfig({
      ...processingConfig,
      mode: method
    });
  };

  // Step configuration
  const steps: Step[] = [
    { number: 1, name: 'DATA SOURCE', active: currentStep === 1 },
    { number: 2, name: 'DOCUMENT PROCESSING', active: currentStep === 2 },
    { number: 3, name: 'EXECUTE & FINISH', active: currentStep === 3 }
  ];

  // Navigate to Knowledge List
  const handleBack = () => {
    navigate('/knowledge');
  };

  const handleNext = async () => {
    if (currentStep === 1 && selectedDataSource === DataSourceType.UploadFiles) {
      await fetchProcessingConfig();
    }

    if (currentStep === 1 && selectedDataSource === DataSourceType.WebScraping) {
      if (!crawlResults || selectedPages.size === 0) {
        alert('Please crawl the website and select at least one page to proceed.');
        return;
      }
      await fetchProcessingConfig();
    }

    if (currentStep === 2) {
      setIsCreatingDataset(true);

      if (selectedDataSource === DataSourceType.UploadFiles && processingConfig && crawlResults) {
        if (knowledgeId) {
          // const selectedData = (crawlResults.data ?? []).filter((_: any, index: number) => selectedPages.has(index));
          addDocument(knowledgeId, crawlResults.data).then(async (res: any) => {
            await simulateApiDelay(1500);
            setIsCreatingDataset(false);
            setDatasetResponse(res);
          }).catch((error) => {
            console.error('Failed to add document:', error);
            setIsCreatingDataset(false);
          });
        } else {
          // const selectedData = (crawlResults.data ?? []).filter((_: any, index: number) => selectedPages.has(index));
          initializeKnowledgeBase(crawlResults.data).then(async (res: any) => {
            await simulateApiDelay(1500);
            setIsCreatingDataset(false);
            setDatasetResponse(res);
          }).catch((error) => {
            console.error('Failed to initialize knowledge base:', error);
            setIsCreatingDataset(false);
          });
        }
      } else if (selectedDataSource === DataSourceType.WebScraping && crawlResults && selectedPages.size > 0 && processingConfig) {
        if (knowledgeId) {
          const selectedData = (crawlResults.data ?? []).filter((_: any, index: number) => selectedPages.has(index));
          addDocument(knowledgeId, selectedData).then(async (res: any) => {
            await simulateApiDelay(1500);
            setIsCreatingDataset(false);
            setDatasetResponse(res);
          }).catch((error) => {
            console.error('Failed to add document:', error);
            setIsCreatingDataset(false);
          });
        } else {
          const selectedData = (crawlResults.data ?? []).filter((_: any, index: number) => selectedPages.has(index));
          initializeKnowledgeBase(selectedData).then(async (res: any) => {
            await simulateApiDelay(1500);
            setIsCreatingDataset(false);
            setDatasetResponse(res);
          }).catch((error) => {
            console.error('Failed to initialize knowledge base:', error);
            setIsCreatingDataset(false);
          });
        }
      }

      setSelectedDataSource(undefined);
      setData(null);
    }

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadWrapper = async (files: FileList | null) => {
    await handleFileUpload(files);
    // Clear the file input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreviewChunk = () => {
    if (selectedDataSource === DataSourceType.WebScraping) {
      fetchWebsiteIndexingEstimate();
    } else {
      fetchIndexingEstimate();
    }
  };

  const canPreview = Boolean((selectedDataSource === DataSourceType.UploadFiles && uploadedFile) ||
    (selectedDataSource === DataSourceType.WebScraping && crawlResults && selectedPages.size > 0));

  const handleNavigateToDocument = () => {
    if (datasetResponse) {
      navigate(`/knowledge/${datasetResponse?.id}/documents`);
    }
  };

  return (
    <div className="h-[calc(100vh-70px)] bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-3 py-3 sm:px-4 sm:py-3 pb-[20px] sm:pb-[42px]">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>

            {/* Progress Steps */}
            <div className="flex items-center justify-center w-auto">
              <StepIndicator steps={steps} />
            </div>
            <div className="hidden sm:block"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='flex flex-col lg:flex-row flex-1 overflow-hidden bg-white'>
        <div className={`${currentStep === 3 ? `w-full` : `w-full lg:flex-1`} max-w-full lg:max-w-5xl mx-auto px-3 py-4 sm:px-4 sm:py-6 overflow-y-auto`}>
          {/* Step 1: Data Source */}
          {currentStep === 1 && (
            <div>
              {!knowledgeId && <h2 className="text-sm sm:text-base mb-3">Data Source</h2>}

              {/* Data Source Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {/* Website Data Source */}
                <div
                  onClick={() => setSelectedDataSource(DataSourceType.WebScraping)}
                  className={`
                    border-2 rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-200
                    ${selectedDataSource === DataSourceType.WebScraping
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    <span className="text-sm font-medium">Sync from website</span>
                  </div>
                </div>

                <div
                  onClick={() => setSelectedDataSource(DataSourceType.UploadFiles)}
                  className={`
                    border-2 rounded-lg p-4 cursor-pointer transition-all duration-200
                    ${selectedDataSource === DataSourceType.UploadFiles
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium">Import from file</span>
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              {selectedDataSource === DataSourceType.UploadFiles && (
                <FileUploadSection
                  uploadedFile={uploadedFile}
                  isUploading={isUploading}
                  dragActive={dragActive}
                  onFileUpload={handleFileUploadWrapper}
                  onBrowseClick={handleBrowseClick}
                  onRemoveFile={removeFile}
                  onDrag={handleDrag}
                  onDrop={handleDrop}
                  fileInputRef={fileInputRef}
                />
              )}
              {/* Website Crawl Section */}
              {selectedDataSource === DataSourceType.WebScraping && (
                <WebsiteCrawlSection
                  websiteUrl={websiteUrl}
                  setWebsiteUrl={setWebsiteUrl}
                  selectedProvider={selectedProvider}
                  setSelectedProvider={setSelectedProvider}
                  crawlOptions={crawlOptions}
                  setCrawlOptions={setCrawlOptions}
                  isCrawling={isCrawling}
                  crawlResults={crawlResults}
                  selectedPages={selectedPages}
                  isOptionsExpanded={isOptionsExpanded}
                  setIsOptionsExpanded={setIsOptionsExpanded}
                  onStartCrawl={startWebsiteCrawl}
                  onPageSelection={handlePageSelection}
                  onSelectAll={handleSelectAll}
                  onResetResults={resetCrawlResults}
                />
              )}
            </div>
          )}

          {/* Step 2: Document Processing */}
          {currentStep === 2 && (
            <div>
              <ProcessingConfiguration
                processingConfig={processingConfig}
                isLoadingConfig={isLoadingConfig}
                onUpdateProcessingRule={updateProcessingRule}
                onUpdateSegmentationConfig={updateSegmentationConfig}
                onUpdateIndexingMethod={updateIndexingMethod}
                onPreviewChunk={handlePreviewChunk}
                isLoadingPreview={isLoadingPreview}
                canPreview={canPreview}
              />
            </div>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <SuccessStep
              datasetResponse={datasetResponse}
              processingConfig={processingConfig}
              isCreatingDataset={isCreatingDataset}
              onNavigateToDocument={handleNavigateToDocument}
              selectedDataSource={selectedDataSource}
              crawlResults={crawlResults}
              selectedPages={selectedPages}
            />
          )}

          {/* Navigation Buttons */}
          {currentStep !== 3 && (
            <div className="flex justify-between pt-4">
              <button
                onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : handleBack()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {currentStep === 1 ? 'Cancel' : 'Previous step'}
              </button>

              {currentStep < 3 ? (
                (crawlResults || uploadedFile) && (
                  <button
                    onClick={() => handleNext()}
                    disabled={isCreatingDataset}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(currentStep === 1 && isLoadingConfig) ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </>
                    ) : currentStep === 2 ? (
                      'Save & Process'
                    ) : (
                      'Next'
                    )}
                  </button>
                )
              ) : datasetResponse ? (
                <button
                  onClick={handleNavigateToDocument}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Document
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={isCreatingDataset}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingDataset ? 'Creating...' : 'Create Knowledge'}
                </button>
              )}

            </div>
          )}
        </div>

        {/* Preview Panel */}
        {currentStep !== 3 && (
          <div className="w-full lg:flex-1 lg:max-w-5xl mx-auto px-3 py-4 sm:px-4 sm:py-6 overflow-y-auto border-t lg:border-t-0 lg:border-l border-gray-200">
            {currentStep === 2 && (
              <PreviewPanel
                selectedDataSource={selectedDataSource}
                crawlResults={crawlResults}
                selectedPages={selectedPages}
                isLoadingPreview={isLoadingPreview}
                selectedPreviewDocument={selectedPreviewDocument}
                onPreviewDocumentChange={setSelectedPreviewDocument}
                onFetchPreview={handlePreviewChunk}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateDocument;
